/**
 * El formulario de busqueda.
 *
 * ===========================================================================
 * MARITIMO Y AEREO SON DOS EXPERIENCIAS, NO UN DESPLEGABLE
 *
 * El modo dejo de ser un campo mas. Es un selector de PESTAÑAS arriba del todo,
 * y lo que hay debajo cambia entero:
 *
 *   MARITIMO   POL, POD, equipo, fecha. Lo de siempre.
 *   AEREO      aeropuertos de origen y destino, peso real, volumen, bultos
 *              --opcional-- y fecha. Ni equipo, ni contenedores.
 *
 * No es cosmetica. Los dos formularios NO comparten campos obligatorios: en
 * aereo el equipo no existe --una tarifa aerea no ofrece cajas-- y el peso es
 * lo que hace de filtro, porque el peso elige el escalon y el escalon puede no
 * existir [H8][H80]. Un desplegable con siete valores dejaba el campo "Equipo"
 * en pantalla pidiendo un dato que la peticion aerea ni siquiera manda, y el
 * peso escondido en ninguna parte.
 *
 * LOS CAMPOS DE UN MODO NO SE TIRAN AL CAMBIAR AL OTRO. Se ocultan, igual que
 * las zonas de la aplicacion: quien prueba una ruta por aire y vuelve al mar no
 * tiene que teclear otra vez sus puertos. Lo que NO se comparte es el par de
 * puntos --un POL maritimo y un AOL son campos distintos con autocompletados
 * distintos-- porque compartirlos dejaria "CNSHA" escrito en un campo que solo
 * propone aeropuertos, y CNSHA no es PVG.
 *
 * ---------------------------------------------------------------------------
 * Dos cosas mas que no son cosmeticas:
 *
 * AUTOCOMPLETADO. Propone, no decide. Cada sugerencia trae su pais y elegirla
 * es lo que lo fija; mientras el agente no elija, el pais NO viaja en la
 * peticion y la API responde con la desambiguacion. Editar el texto a mano
 * despues de haber elegido borra el pais: un pais pegado a un puerto que ya no
 * es el que dice el campo es peor que no tener pais. [Invariante 6][H51]
 *
 * DESAMBIGUACION. El 409 se pinta con sus opciones y ahi se para. No hay
 * "elegir la mas probable", ni preseleccion, ni un reintento automatico con la
 * primera: elegir por el agente es exactamente lo que manda la carga al
 * continente equivocado en silencio.
 */

import type {
  CampoAmbiguo,
  EquipoCatalogo,
  OpcionAmbigua,
  RespuestaSugerencias,
  Sugerencia,
} from '../api';
import { sugerirUbicaciones } from '../api';
import { el, literal, vaciar } from '../dom';
import type { Traductor } from '../i18n';
import { armarSugerencias, type FormularioBusqueda } from '../peticiones';

/**
 * Las dos experiencias, y los modos que cada una cubre.
 *
 * MARITIMO agrupa FCL y LCL --son la misma pantalla con otro equipo-- y AEREO
 * es una sola. La pestaña se elige aqui; el modo concreto, en el desplegable de
 * dentro cuando hay mas de uno.
 *
 * [CLAUDE.md, Alcance] "Dentro: FCL y LCL" mas el aereo del bloque 0022..0028.
 * PROYECTO, TERRESTRE, FLUVIAL y FERROVIARIO existen en el enum y no tienen
 * pestaña: el catalogo no tiene tarifas suyas y una pestaña vacia es una puerta
 * que no abre.
 */
export type Experiencia = 'MARITIMO' | 'AEREO';

const EXPERIENCIAS: readonly Experiencia[] = ['MARITIMO', 'AEREO'];

/** Los modos de cada pestaña, en el orden del desplegable. */
const MODOS_DE: Record<Experiencia, readonly string[]> = {
  MARITIMO: ['FCL', 'LCL'],
  AEREO: ['AEREO'],
};

/** A que pestaña pertenece un modo. Lo desconocido cae en MARITIMO, que es
 *  donde estaba antes de que hubiera pestañas. */
export function experienciaDe(modo: string): Experiencia {
  return modo === 'AEREO' ? 'AEREO' : 'MARITIMO';
}

const RETARDO_SUGERENCIAS = 200;

export interface ManejadorBusqueda {
  readonly nodo: HTMLElement;
  valores(): FormularioBusqueda;
  setCargando(cargando: boolean): void;
  setEquipos(equipos: readonly EquipoCatalogo[]): void;
  marcarFaltantes(campos: readonly string[]): void;
  /** Aplica la opcion que el agente eligio en la desambiguacion. */
  aplicarOpcion(campo: 'pol' | 'pod' | 'equipo', opcion: OpcionAmbigua): void;
}

interface CampoPuerto {
  readonly contenedor: HTMLElement;
  readonly entrada: HTMLInputElement;
  /** El desplegable entero: la lista y el aviso de recorte. */
  readonly popup: HTMLElement;
  /** Solo las opciones. Es el `role="listbox"` y no lleva nada mas dentro:
   *  `marcarActivo` recorre sus hijos por indice, y un hijo que no fuera una
   *  opcion desalinearia el teclado de lo que se ve. */
  readonly lista: HTMLElement;
  /** "Hay mas — sigue escribiendo". Fuera del listbox a proposito. */
  readonly mas: HTMLElement;
  readonly chip: HTMLElement;
  pais: string | null;
  /** Las sugerencias que hay pintadas ahora. */
  opciones: Sugerencia[];
  /** Indice de la resaltada, -1 si ninguna. */
  activo: number;
}

export function montarBusqueda(
  t: Traductor,
  opts: {
    readonly inicial: FormularioBusqueda;
    readonly onBuscar: () => void;
    /** El modo CONCRETO --FCL, LCL, AEREO--, no la pestaña: es lo que decide
     *  que equipos pedir al catalogo y que ubicaciones propone el
     *  autocompletado. */
    readonly onModo: (modo: string) => void;
  },
): ManejadorBusqueda {
  const form = el('form', { clase: 'busqueda-forma' });

  let experiencia = experienciaDe(opts.inicial.modo);

  // -------------------------------------------------------------------------
  // Las dos pestañas
  //
  // `role="tablist"` de verdad, con las flechas moviendo la seleccion, porque
  // esto NO es un grupo de botones que hacen cosas: es un selector de cual de
  // los dos paneles se ve. Un lector de pantalla que lo anuncie como dos
  // botones deja al agente sin saber que hay dos formularios.
  // -------------------------------------------------------------------------

  const pestanas = el('div', {
    clase: 'modo-pestanas',
    attrs: { role: 'tablist', 'aria-label': t('busqueda.modo') },
  });

  const botonDe = {} as Record<Experiencia, HTMLElement>;

  for (const x of EXPERIENCIAS) {
    const boton = el('button', {
      clase: 'modo-pestana',
      texto: t(`experiencia.${x}`),
      attrs: {
        type: 'button',
        role: 'tab',
        id: `pestana-${x}`,
        'aria-controls': `panel-${x}`,
        'aria-selected': 'false',
        tabindex: '-1',
      },
      onClick: () => elegirExperiencia(x),
    });
    botonDe[x] = boton;
    pestanas.appendChild(boton);
  }

  pestanas.addEventListener('keydown', (ev) => {
    const tecla = ev.key;
    if (tecla !== 'ArrowLeft' && tecla !== 'ArrowRight') return;
    ev.preventDefault();
    const i = EXPERIENCIAS.indexOf(experiencia);
    const n = EXPERIENCIAS.length;
    const siguiente = EXPERIENCIAS[(i + (tecla === 'ArrowRight' ? 1 : n - 1)) % n];
    elegirExperiencia(siguiente);
    botonDe[siguiente].focus();
  });

  // -------------------------------------------------------------------------
  // Los dos paneles
  //
  // Cada uno con SUS campos de punto. Ver la cabecera: no se comparten, porque
  // un POL y un AOL se autocompletan contra catalogos distintos.
  // -------------------------------------------------------------------------

  const selectModo = el('select', {
    clase: 'control',
    attrs: { id: 'campo-modo', name: 'modo' },
  });
  selectModo.addEventListener('change', () => opts.onModo(selectModo.value));

  const pol = campoPuerto(t, 'pol', opts.inicial.pol, opts.inicial.polPais, modoActual);
  const pod = campoPuerto(t, 'pod', opts.inicial.pod, opts.inicial.podPais, modoActual);

  const selectEquipo = el('select', {
    clase: 'control',
    attrs: { id: 'campo-equipo', name: 'equipo' },
  });

  const fecha = el('input', {
    clase: 'control',
    attrs: {
      id: 'campo-fecha',
      name: 'fecha_embarque',
      type: 'date',
      value: opts.inicial.fechaEmbarque,
      required: 'required',
    },
  });

  // --- Aereo ---------------------------------------------------------------

  const aol = campoPuerto(t, 'aol', opts.inicial.pol, opts.inicial.polPais, modoActual);
  const aod = campoPuerto(t, 'aod', opts.inicial.pod, opts.inicial.podPais, modoActual);

  const pesoKg = numerico('peso_kg', opts.inicial.pesoKg, '0.001');
  const volumenM3 = numerico('volumen_m3', opts.inicial.volumenM3, '0.001');
  const bultos = numerico('bultos', opts.inicial.bultos, '1');

  const boton = el('button', {
    clase: 'boton boton-primario',
    texto: t('busqueda.buscar'),
    attrs: { type: 'submit' },
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    opts.onBuscar();
  });

  const panelMaritimo = el(
    'div',
    {
      clase: 'busqueda',
      attrs: {
        role: 'tabpanel',
        id: 'panel-MARITIMO',
        'aria-labelledby': 'pestana-MARITIMO',
      },
    },
    [
      campo(t('busqueda.pol'), 'campo-pol', pol.contenedor, 'campo campo-ancho'),
      campo(t('busqueda.pod'), 'campo-pod', pod.contenedor, 'campo campo-ancho'),
      campo(t('busqueda.modo'), 'campo-modo', selectModo),
      campo(t('busqueda.equipo'), 'campo-equipo', selectEquipo),
    ],
  );

  const panelAereo = el(
    'div',
    {
      clase: 'busqueda busqueda-aerea',
      attrs: { role: 'tabpanel', id: 'panel-AEREO', 'aria-labelledby': 'pestana-AEREO' },
    },
    [
      campo(t('busqueda.aol'), 'campo-aol', aol.contenedor, 'campo campo-ancho'),
      campo(t('busqueda.aod'), 'campo-aod', aod.contenedor, 'campo campo-ancho'),
      // El peso y el volumen van JUNTOS y en ese orden. No son dos datos
      // sueltos: son las dos mitades de una misma pregunta --cual de los dos
      // pesos manda-- y separarlos invita a rellenar uno solo. [H80]
      campo(t('busqueda.pesoKg'), 'campo-peso_kg', pesoKg, 'campo campo-cifra', {
        pista: t('busqueda.pesoKgPista'),
      }),
      campo(t('busqueda.volumenM3'), 'campo-volumen_m3', volumenM3, 'campo campo-cifra', {
        pista: t('busqueda.volumenM3Pista'),
      }),
      // Opcional, y se dice en la etiqueta. Sin esto un campo vacio parece un
      // campo a medio llenar.
      campo(t('busqueda.bultos'), 'campo-bultos', bultos, 'campo campo-cifra', {
        opcional: t('busqueda.opcional'),
        pista: t('busqueda.bultosPista'),
      }),
    ],
  );

  // La fecha y el boton son de las dos: la fecha decide la vigencia en los dos
  // modos y el boton hace lo mismo. Fuera de los paneles para que no haya dos
  // campos de fecha con el mismo nombre en el mismo formulario.
  const pieComun = el('div', { clase: 'busqueda busqueda-pie' }, [
    campo(t('busqueda.fechaEmbarque'), 'campo-fecha', fecha),
    el('div', { clase: 'campo campo-accion' }, [boton]),
  ]);

  form.append(pestanas, panelMaritimo, panelAereo, pieComun);

  const porNombre: Record<string, HTMLElement> = {
    pol: pol.entrada,
    pod: pod.entrada,
    equipo: selectEquipo,
    fecha_embarque: fecha,
    pol_pais: pol.chip,
    pod_pais: pod.chip,
    modo: selectModo,
    peso_kg: pesoKg,
    volumen_m3: volumenM3,
    bultos,
  };

  aplicarExperiencia();

  return {
    nodo: form,

    valores: () => {
      const aereo = experiencia === 'AEREO';
      // Los puntos salen del panel VISIBLE. Leer los dos y quedarse con el que
      // tenga algo escrito dejaria una busqueda aerea saliendo con el POL
      // maritimo que quedo de antes.
      const origen = aereo ? aol : pol;
      const destino = aereo ? aod : pod;
      return {
        pol: origen.entrada.value,
        polPais: origen.pais,
        pod: destino.entrada.value,
        podPais: destino.pais,
        modo: selectModo.value,
        // Se manda vacio en aereo y `armarBusqueda` lo deja fuera del cuerpo:
        // el desplegable de equipos ni siquiera esta en pantalla.
        equipo: aereo ? '' : selectEquipo.value,
        pesoKg: pesoKg.value,
        volumenM3: volumenM3.value,
        bultos: bultos.value,
        fechaEmbarque: fecha.value,
        // La declaracion de carga vive fuera de este formulario --se repinta
        // sola-- y quien monta la vista la vuelve a pegar. Aqui viaja la que
        // llego, para no perderla al releer los valores.
        carga: opts.inicial.carga,
      };
    },

    setCargando(cargando) {
      boton.textContent = cargando ? t('busqueda.buscando') : t('busqueda.buscar');
      boton.toggleAttribute('disabled', cargando);
      form.classList.toggle('cargando', cargando);
    },

    setEquipos(equipos) {
      const previo = selectEquipo.value;
      vaciar(selectEquipo);
      for (const e of equipos) {
        // [CLAUDE.md, Idioma] El desplegable manda el CODIGO. El nombre es solo
        // lo que se pinta; guardar la etiqueta seria guardar la traduccion.
        selectEquipo.appendChild(
          el('option', {
            texto: `${e.codigo} · ${e.nombre}`,
            attrs: { value: e.codigo },
          }),
        );
      }
      if (equipos.some((e) => e.codigo === previo)) selectEquipo.value = previo;
    },

    marcarFaltantes(campos) {
      for (const nodo of Object.values(porNombre)) nodo.classList.remove('falta');
      for (const c of campos) {
        // El POL y el POD de un fallo aereo marcan los campos del panel aereo:
        // la API los llama igual --la ruta es la ruta-- y quien lo lee tiene
        // delante el otro formulario.
        if (experiencia === 'AEREO' && c === 'pol') aol.entrada.classList.add('falta');
        else if (experiencia === 'AEREO' && c === 'pod')
          aod.entrada.classList.add('falta');
        else if (experiencia === 'AEREO' && c === 'pol_pais')
          aol.chip.classList.add('falta');
        else if (experiencia === 'AEREO' && c === 'pod_pais')
          aod.chip.classList.add('falta');
        else porNombre[c]?.classList.add('falta');
      }
    },

    aplicarOpcion(campo, opcion) {
      if (campo === 'equipo') {
        if (opcion.codigo !== undefined) selectEquipo.value = opcion.codigo;
        return;
      }
      const aereo = experiencia === 'AEREO';
      const destino = campo === 'pol' ? (aereo ? aol : pol) : aereo ? aod : pod;
      // El codigo antes que el nombre: es la via mas fuerte de resolucion y
      // deja la busqueda resolviendo por codigo y no por nombre ni por alias.
      // En aereo el IATA va primero, que es lo que el proveedor escribe: un
      // aeropuerto con LOCODE y IATA se resuelve mejor por su designador.
      destino.entrada.value = aereo
        ? (opcion.iata ?? opcion.locode ?? opcion.nombre ?? '')
        : (opcion.locode ?? opcion.iata ?? opcion.nombre ?? '');
      fijarPais(t, destino, opcion.pais ?? null);
    },
  };

  /** El modo concreto que hay elegido ahora. Lo lee el autocompletado, que
   *  propone aeropuertos o puertos segun cual sea. */
  function modoActual(): string {
    return selectModo.value;
  }

  function elegirExperiencia(x: Experiencia): void {
    if (x === experiencia) return;
    experiencia = x;
    aplicarExperiencia();
    // El modo cambio, asi que el catalogo de equipos y el autocompletado
    // tambien: quien monta esto recarga lo que haga falta.
    opts.onModo(selectModo.value);
  }

  /**
   * Pinta la pestaña elegida y deja la otra oculta.
   *
   * Los paneles se OCULTAN, no se vacian: volver al mar no cuesta teclear los
   * puertos otra vez. Es la misma decision que toman las zonas de la aplicacion
   * [CLAUDE.md, "Cambiar de zona no tira lo que habia"].
   */
  function aplicarExperiencia(): void {
    const modos = MODOS_DE[experiencia];
    const previo = selectModo.value;
    vaciar(selectModo);
    for (const m of modos) {
      selectModo.appendChild(
        el('option', { texto: t(`modo.${m}`), attrs: { value: m } }),
      );
    }
    selectModo.value = modos.includes(previo) ? previo : modos[0];

    for (const x of EXPERIENCIAS) {
      const activa = x === experiencia;
      botonDe[x].setAttribute('aria-selected', activa ? 'true' : 'false');
      botonDe[x].setAttribute('tabindex', activa ? '0' : '-1');
      botonDe[x].classList.toggle('modo-pestana-activa', activa);
    }
    panelMaritimo.hidden = experiencia !== 'MARITIMO';
    panelAereo.hidden = experiencia !== 'AEREO';
  }

  /**
   * Un campo con su etiqueta.
   *
   * `opcional` NO es un `title` ni un asterisco: es texto al lado del nombre.
   * Un campo que se puede dejar vacio y uno que falta se ven igual, y averiguar
   * cual es cual pasando el raton no es averiguarlo.
   */
  function campo(
    texto: string,
    para: string,
    control: HTMLElement,
    clase = 'campo',
    extra?: { readonly opcional?: string; readonly pista?: string },
  ): HTMLElement {
    return el('div', { clase }, [
      el('label', { clase: 'etiqueta', attrs: { for: para } }, [
        el('span', { texto }),
        extra?.opcional === undefined
          ? null
          : el('span', { clase: 'etiqueta-opcional', texto: extra.opcional }),
      ]),
      control,
      extra?.pista === undefined
        ? null
        : el('p', {
            clase: 'campo-pista',
            texto: extra.pista,
            attrs: { id: `${para}-pista` },
          }),
    ]);
  }

  /**
   * Una cifra de la carga.
   *
   * `inputmode="decimal"` y no `type="number"`: los steppers de un `number` en
   * un campo de peso no sirven para nada --nadie sube un kilo de uno en uno-- y
   * la rueda del raton sobre el campo cambia el valor sin que nadie lo pida, que
   * es un peso equivocado sin ninguna pulsacion. `type="text"` mantiene el valor
   * como CADENA, que es lo que este numero tiene que ser hasta la API
   * [CLAUDE.md, Dinero].
   */
  function numerico(nombre: string, valor: string, paso: string): HTMLInputElement {
    return el('input', {
      clase: 'control control-cifra',
      attrs: {
        id: `campo-${nombre}`,
        name: nombre,
        type: 'text',
        inputmode: paso === '1' ? 'numeric' : 'decimal',
        autocomplete: 'off',
        value: valor,
        'aria-describedby': `campo-${nombre}-pista`,
      },
    });
  }
}

// ---------------------------------------------------------------------------
// Campo de puerto con autocompletado
// ---------------------------------------------------------------------------

function fijarPais(t: Traductor, campo: CampoPuerto, pais: string | null): void {
  campo.pais = pais;
  campo.chip.textContent = pais ?? t('busqueda.sinPais');
  campo.chip.classList.toggle('chip-vacio', pais === null);
  campo.chip.classList.remove('falta');
}

/**
 * Un campo de puerto con autocompletado.
 *
 * ---------------------------------------------------------------------------
 * EL FOCO NO SE MUEVE NUNCA DE LA ENTRADA
 *
 * Es el patron de combobox con `aria-activedescendant`: las sugerencias no son
 * focusables, y la que esta resaltada se declara por atributo en vez de por
 * foco. Suena a detalle de accesibilidad y es lo que hace que la lista se pueda
 * usar con el raton.
 *
 * La version anterior cerraba la lista en el `blur` de la entrada, con 150 ms
 * de margen para que el clic llegara antes. No llegaba: el `blur` ocurre en el
 * `mousedown`, o sea al APRETAR el boton, y el `click` no se emite hasta
 * soltarlo. Cualquier pulsacion que durase mas de 150 ms --que es casi
 * cualquier pulsacion humana deliberada-- vaciaba la lista con el boton todavia
 * apretado, y al soltar ya no habia nada debajo del cursor que pudiera recibir
 * el clic. Los nombres se veian y no se podian elegir.
 *
 * El `preventDefault` del `mousedown` de abajo es el arreglo: impide que la
 * entrada pierda el foco, asi que no hay `blur`, no hace falta ningun plazo, y
 * el `click` aterriza sobre un elemento que sigue existiendo. Un plazo mas
 * largo habria sido la misma apuesta con otro numero.
 * ---------------------------------------------------------------------------
 */
function campoPuerto(
  t: Traductor,
  /** Da nombre al campo y a los ids de sus sugerencias. `aol`/`aod` son los del
   *  panel aereo: los mismos dos puntos de la ruta con el nombre que usa el
   *  proveedor cuando escribe una tarifa de avion [CLAUDE.md, Glosario]. */
  nombre: 'pol' | 'pod' | 'aol' | 'aod',
  valorInicial: string,
  paisInicial: string | null,
  modoActual: () => string,
): CampoPuerto {
  const idLista = `sugerencias-${nombre}`;
  const esAereo = nombre === 'aol' || nombre === 'aod';

  const entrada = el('input', {
    clase: 'control',
    attrs: {
      id: `campo-${nombre}`,
      name: nombre,
      type: 'text',
      autocomplete: 'off',
      role: 'combobox',
      'aria-expanded': 'false',
      'aria-autocomplete': 'list',
      'aria-controls': idLista,
      value: valorInicial,
      placeholder: esAereo
        ? t('busqueda.escribeParaBuscarAereo')
        : t('busqueda.escribeParaBuscar'),
    },
  });

  const lista = el('div', {
    clase: 'sugerencias-lista',
    attrs: { id: idLista, role: 'listbox' },
  });

  // `role="status"`: cuando aparece, un lector de pantalla lo anuncia sin que
  // el foco se mueva. Es informacion sobre la lista, no una opcion de ella, y
  // por eso vive fuera del listbox: dentro seria un elemento sin `role=option`
  // en un contenedor que promete que todos lo son.
  const mas = el('div', {
    clase: 'sugerencias-mas',
    attrs: { role: 'status', hidden: 'hidden' },
    texto: t('busqueda.hayMas'),
  });

  const popup = el('div', { clase: 'sugerencias', attrs: { hidden: 'hidden' } }, [
    lista,
    mas,
  ]);

  const chip = el('span', { clase: 'chip', attrs: { title: t('busqueda.pais') } });

  const contenedor = el('div', { clase: 'campo-puerto' }, [
    el('div', { clase: 'campo-puerto-fila' }, [entrada, chip]),
    popup,
  ]);

  const campo: CampoPuerto = {
    contenedor,
    entrada,
    popup,
    lista,
    mas,
    chip,
    pais: null,
    opciones: [],
    activo: -1,
  };
  fijarPais(t, campo, paisInicial);

  let temporizador: ReturnType<typeof setTimeout> | undefined;
  /** Contador de peticiones: una respuesta lenta de "SHA" no puede pisar la
   *  lista de "SHANGHAI" escrito despues. */
  let generacion = 0;

  function cerrar(): void {
    lista.replaceChildren();
    popup.hidden = true;
    mas.hidden = true;
    campo.opciones = [];
    campo.activo = -1;
    entrada.setAttribute('aria-expanded', 'false');
    entrada.removeAttribute('aria-activedescendant');
  }

  /** Resalta la sugerencia `i`, o ninguna con -1. */
  function marcarActivo(i: number): void {
    campo.activo = i;
    const filas = lista.children;
    for (let k = 0; k < filas.length; k++) {
      const fila = filas[k] as HTMLElement;
      const activa = k === i;
      fila.classList.toggle('sugerencia-activa', activa);
      fila.setAttribute('aria-selected', activa ? 'true' : 'false');
    }
    if (i < 0) {
      entrada.removeAttribute('aria-activedescendant');
      return;
    }
    const fila = filas[i] as HTMLElement;
    entrada.setAttribute('aria-activedescendant', fila.id);
    // Opcional: no existe en todos los entornos donde corre este codigo.
    fila.scrollIntoView?.({ block: 'nearest' });
  }

  function elegir(u: Sugerencia): void {
    // El CODIGO antes que el nombre: es la via mas fuerte de resolucion, y deja
    // la busqueda resolviendo por codigo y no por un alias sin firmar [H52].
    //
    // Cual de los dos codigos manda depende del modo, y no es un detalle: el
    // aeropuerto de Shanghai-Pudong tiene IATA PVG, y CNSHA --que es lo que
    // pondria el LOCODE-- es el PUERTO. Escribir CNSHA en un campo aereo es
    // pedir tarifas de un sitio que no es.
    entrada.value = esAereo
      ? (u.iata ?? u.locode ?? u.nombre)
      : (u.locode ?? u.iata ?? u.nombre);
    fijarPais(t, campo, u.pais);
    cerrar();
    entrada.focus();
  }

  entrada.addEventListener('input', () => {
    // Al escribir a mano, el pais elegido deja de valer: quedaria pegado a un
    // texto que ya no es el que se eligio.
    if (campo.pais !== null) fijarPais(t, campo, null);

    if (temporizador !== undefined) clearTimeout(temporizador);
    const query = armarSugerencias(entrada.value, modoActual());
    if (query === null) {
      cerrar();
      return;
    }

    temporizador = setTimeout(() => {
      const mia = ++generacion;
      void sugerirUbicaciones(query).then(
        (respuesta) => {
          if (mia !== generacion) return;
          pintarSugerencias(t, campo, respuesta, nombre, elegir, marcarActivo);
        },
        // El autocompletado que falla no interrumpe: el agente puede escribir
        // el LOCODE y buscar igual. El error de verdad, si lo hay, saldra en la
        // busqueda con su mensaje.
        () => {
          if (mia === generacion) cerrar();
        },
      );
    }, RETARDO_SUGERENCIAS);
  });

  entrada.addEventListener('keydown', (ev) => {
    const tecla = ev.key;
    const abierta = !popup.hidden && campo.opciones.length > 0;

    if (tecla === 'Escape') {
      // preventDefault: en algunos navegadores Escape sobre un input deshace lo
      // escrito, y aqui solo tiene que cerrar la lista.
      if (abierta) ev.preventDefault();
      cerrar();
      return;
    }

    if (tecla === 'Tab') {
      cerrar();
      return;
    }

    if (tecla === 'ArrowDown' || tecla === 'ArrowUp') {
      if (!abierta) return;
      // Sin esto el cursor salta al principio o al final del texto mientras se
      // recorre la lista.
      ev.preventDefault();
      const n = campo.opciones.length;
      marcarActivo(
        tecla === 'ArrowDown'
          ? (campo.activo + 1) % n
          : (campo.activo <= 0 ? n : campo.activo) - 1,
      );
      return;
    }

    if (tecla === 'Enter') {
      // Enter con la lista abierta pero sin nada resaltado NO elige nada: envia
      // el formulario. Es lo que espera quien escribio el LOCODE entero y no
      // quiso abrir la lista para nada.
      if (!abierta || campo.activo < 0) return;
      ev.preventDefault();
      elegir(campo.opciones[campo.activo]);
    }
  });

  // EL ARREGLO. `mousedown` es lo que dispara el `blur` de la entrada, y
  // prevenirlo evita que el foco se mueva: la lista sigue en pie cuando el
  // boton se suelta y el `click` de abajo tiene donde aterrizar, dure lo que
  // dure la pulsacion.
  popup.addEventListener('mousedown', (ev) => ev.preventDefault());

  // El foco solo se pierde de verdad al tabular o al pulsar en otro control.
  // `relatedTarget` dice a donde fue; si sigue dentro del campo, no se cierra.
  contenedor.addEventListener('focusout', (ev) => {
    const destino = ev.relatedTarget;
    if (destino instanceof Node && contenedor.contains(destino)) return;
    cerrar();
  });

  // Pulsar fuera cierra. Hace falta ademas del `focusout` porque pulsar sobre
  // una zona no focusable de la pagina no mueve el foco y no habria disparado
  // nada. Se limpia sola cuando el campo deja de estar en el documento --el
  // formulario se remonta entero al cambiar de idioma-- para no ir dejando un
  // oyente por cada remontaje.
  function alPulsarFuera(ev: Event): void {
    if (!contenedor.isConnected) {
      document.removeEventListener('mousedown', alPulsarFuera, true);
      return;
    }
    if (!contenedor.contains(ev.target as Node)) cerrar();
  }
  document.addEventListener('mousedown', alPulsarFuera, true);

  return campo;
}

function pintarSugerencias(
  t: Traductor,
  campo: CampoPuerto,
  respuesta: RespuestaSugerencias,
  nombre: string,
  elegir: (u: Sugerencia) => void,
  marcarActivo: (i: number) => void,
): void {
  const { ubicaciones } = respuesta;
  const esAereo = nombre === 'aol' || nombre === 'aod';
  /** El codigo con el que se va a resolver. Ver `elegir`. */
  const codigoVisible = (u: Sugerencia): string | null =>
    esAereo ? (u.iata ?? u.locode) : (u.locode ?? u.iata);

  vaciar(campo.lista);
  campo.opciones = [...ubicaciones];
  campo.activo = -1;
  campo.entrada.removeAttribute('aria-activedescendant');

  // El aviso de recorte solo tiene sentido junto a una lista: sin resultados no
  // hay nada que recortar.
  campo.mas.hidden = !respuesta.hay_mas || ubicaciones.length === 0;

  if (ubicaciones.length === 0) {
    campo.popup.hidden = true;
    campo.entrada.setAttribute('aria-expanded', 'false');
    return;
  }

  ubicaciones.forEach((u, i) => {
    const fila = el(
      'div',
      {
        clase: 'sugerencia',
        // No es focusable a proposito: el foco se queda en la entrada y la
        // opcion resaltada se declara con `aria-activedescendant`.
        attrs: {
          id: `sugerencia-${nombre}-${String(i)}`,
          role: 'option',
          'aria-selected': 'false',
        },
        onClick: () => elegir(u),
      },
      [
        el('span', { clase: 'sugerencia-nombre', texto: u.nombre }),
        // El pais siempre visible: es lo que distingue las dos Manzanillo, y
        // una lista que no lo muestra convierte la eleccion en una moneda al
        // aire. [Invariante 6][H51]
        el('span', { clase: 'sugerencia-pais', texto: u.pais }),
        // El codigo que manda en este modo, y solo ese: enseñar los dos deja
        // al agente eligiendo entre dos codigos del mismo punto sin saber cual
        // se va a mandar.
        codigoVisible(u) === null ? null : el('code', { texto: codigoVisible(u)! }),
        // La capa. Las curadas no llevan marca --son la norma, y marcarlas
        // todas seria ruido en el 100% de las listas cortas--; la de
        // referencia si, porque elegirla es elegir un puerto contra el que hoy
        // no hay ninguna tarifa cargada. El `title` lo dice con palabras: el
        // signo por si solo no explica nada a quien lo ve por primera vez.
        u.procedencia === 'CURADA'
          ? null
          : el('span', {
              clase: 'sugerencia-referencia',
              texto: t('busqueda.deReferencia'),
              attrs: { title: t('busqueda.deReferenciaDetalle') },
            }),
        // [H52] Se ofrece como opcion, no como respuesta: marcada, para que el
        // agente sepa que esta firmando la interpretacion de un alias que nadie
        // confirmo. Al elegirla se manda el LOCODE, no el alias.
        u.requiere_confirmacion
          ? el('span', {
              clase: 'aviso-linea',
              texto: t('busqueda.aliasSinConfirmar'),
            })
          : null,
      ],
    );
    // El raton y el teclado comparten el resaltado: pasar por encima mueve lo
    // mismo que mueven las flechas, y no hay dos nociones de "la elegida".
    fila.addEventListener('mouseenter', () => marcarActivo(i));
    campo.lista.appendChild(fila);
  });

  campo.popup.hidden = false;
  campo.entrada.setAttribute('aria-expanded', 'true');
}

// ---------------------------------------------------------------------------
// Desambiguacion
// ---------------------------------------------------------------------------

/**
 * El 409 de la API, con sus opciones y sin ninguna preseleccionada.
 *
 * El titulo sale del CODIGO --`motivo`, traducido-- y el texto de la API se
 * pinta debajo como detalle. Es la regla de errores.ts: el codigo es lo que
 * consume el cliente, el mensaje es para el humano. Ramificar sobre el texto
 * se rompe en cuanto se corrige una tilde, y esta pantalla es bilingue.
 */
export function pintarDesambiguacion(
  t: Traductor,
  campos: readonly CampoAmbiguo[],
  onElegir: (campo: 'pol' | 'pod' | 'equipo', opcion: OpcionAmbigua) => void,
): HTMLElement {
  return el('section', { clase: 'panel panel-eleccion' }, [
    el('h2', { texto: t('desambiguacion.titulo') }),
    ...campos.map((c) =>
      el('article', { clase: 'eleccion' }, [
        el('header', { clase: 'eleccion-cabecera' }, [
          el('span', { clase: 'eleccion-campo', texto: t(`busqueda.${c.campo}`) }),
          el('span', {
            clase: 'etiqueta-motivo',
            texto: t(`desambiguacion.${c.motivo}`),
          }),
          // Lo que el agente escribio, tal cual.
          literal(c.valor, 'literal literal-destacado'),
        ]),
        el('p', { clase: 'eleccion-mensaje', texto: c.mensaje }),
        c.opciones.length === 0
          ? el('p', { clase: 'eleccion-mensaje', texto: t('desambiguacion.sinOpciones') })
          : el(
              'div',
              { clase: 'eleccion-opciones' },
              c.opciones.map((o) =>
                el(
                  'button',
                  {
                    clase: 'boton boton-opcion',
                    attrs: { type: 'button' },
                    onClick: () => onElegir(c.campo, o),
                  },
                  [
                    el('span', {
                      clase: 'opcion-nombre',
                      texto: o.nombre ?? o.codigo ?? '',
                    }),
                    o.pais === undefined
                      ? null
                      : el('span', { clase: 'chip', texto: o.pais }),
                    o.locode == null ? null : el('code', { texto: o.locode }),
                    o.tipo === undefined
                      ? null
                      : el('span', { clase: 'tenue', texto: o.tipo }),
                    o.modo === undefined
                      ? null
                      : el('span', { clase: 'tenue', texto: o.modo }),
                    el('span', {
                      clase: 'opcion-accion',
                      texto: t('desambiguacion.elegir'),
                    }),
                  ],
                ),
              ),
            ),
      ]),
    ),
  ]);
}
